import { JHA_THEMES, setJhaWcTheme } from '@banno/jha-wc/src/styles/theme-provider';
import { DiContainer, InjectionToken } from '@jack-henry/frontend-utils/di';
import { JhHttpClientCached } from '@jack-henry/frontend-utils/http';
import { DynamicComponentRegistry } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { AnalyticsEvent, AnalyticsService, NullAnalyticsService } from '@treasury/core/analytics';
import { CoBrowseService } from '@treasury/core/co-browse';
import { ConfigurationService } from '@treasury/core/config';
import { TmHttpClient } from '@treasury/core/http';
import { LoggingService } from '@treasury/core/logging';
import { LegacyNavigationService, NavigationService } from '@treasury/core/navigation';
import {
    createRealtimeService,
    NullRealtimeService,
    RealtimeService,
} from '@treasury/core/real-time';
import {
    AccountService,
    ChannelAuthenticationService,
} from '@treasury/domain/channel/services/account';
import { EntitlementsService } from '@treasury/domain/entitlements';
import { AuthenticationService, AuthFlowEvent } from '@treasury/domain/services/authentication';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import {
    LogoutManager,
    OpenIdleDialogToken,
    OpenTimeAccessDialogToken,
} from '@treasury/domain/services/logout';
import { OpenIdentityDialogToken, TmHttpClientMfa } from '@treasury/domain/shared';
import { openIdentityDialog } from '@treasury/omega/domain-components/identity-verification-dialog';
import { openIdleDialog } from '@treasury/omega/domain-components/idle-logout-dialog';
import { openTimeAccessDialog } from '@treasury/omega/domain-components/time-access-logout-dialog';
import { OmegaDialogService } from '@treasury/omega/services/omega-dialog';
import {
    DrawerConfirmationPromptToken,
    SideDrawerPromptFn,
} from '@treasury/presentation/services/side-drawer';
import { SessionStorageService, WindowService } from '@treasury/utils/services';

export const channelDiModule = angular
    .module('channel.di', [])
    .constant('TmDi', new DiContainer())
    .config([
        'TmDi',
        function (di: DiContainer) {
            initDi(di);
            setJhaWcTheme(JHA_THEMES.DESIGN_SYSTEM, true);
        },
    ])
    .factory('tmNavService', ['TmDi', (di: DiContainer) => di.get(NavigationService)]);

async function initDi(di: DiContainer) {
    di.provide(OpenIdentityDialogToken as unknown as InjectionToken<unknown>, openIdentityDialog);
    di.provide(OpenIdleDialogToken as unknown as InjectionToken<unknown>, openIdleDialog);
    di.provide(DrawerConfirmationPromptToken, openDialogForSideDrawer);
    di.provide(DynamicComponentRegistry, new DynamicComponentRegistry(true));
    di.provide(
        OpenTimeAccessDialogToken as unknown as InjectionToken<unknown>,
        openTimeAccessDialog
    );
    di.provide(TmHttpClient, TmHttpClientMfa);
    di.provide(NavigationService, LegacyNavigationService);
    di.provideFactoryAsync(
        AnalyticsService,
        [
            ConfigurationService,
            FeatureFlagService,
            AuthenticationService,
            AccountService,
            LoggingService,
        ] as const,
        async (config, ffService, auth, accountService, logger) => {
            await auth.authenticated$.toPromise();
            await ffService.ready$.toPromise();

            try {
                const analyticsEnabled = await ffService.isEnabled(Feature.Analytics);
                const analyticsService = analyticsEnabled
                    ? new AnalyticsService(config, logger, ffService)
                    : NullAnalyticsService;

                const user = await accountService.getCurrentUser();

                await analyticsService.init();
                analyticsService.identify(user);

                return analyticsService;
            } catch (e) {
                console.error('Failed to instantiate AnalyticsService');
                throw e;
            }
        },
        NullAnalyticsService
    );

    di.provideFactoryAsync(
        RealtimeService,
        [
            LoggingService,
            ConfigurationService,
            WindowService,
            AuthenticationService,
            FeatureFlagService,
            TmHttpClient,
        ] as const,
        async (logger, config, window, auth, ffService, http) => {
            await auth.authenticated$.toPromise();
            await ffService.ready$.toPromise();

            const useModern = await ffService.isEnabled(Feature.ModernRealtime);
            return createRealtimeService(useModern, {
                config,
                window,
                http,
                logger,
            });
        },
        NullRealtimeService
    );

    di.provideFactoryAsync(
        FeatureFlagService,
        [
            TmHttpClient,
            SessionStorageService,
            ConfigurationService,
            AuthenticationService,
            AccountService,
            LoggingService,
        ] as const,
        async (http, storage, config, auth, accountService, logger) => {
            await auth.authenticated$.toPromise();

            const { institutionId } = config;
            const ffService = new FeatureFlagService(http, storage, config, logger);

            await initFfService(ffService, accountService, institutionId);

            return ffService;
        }
    );

    di.provideFactory(LoggingService, [WindowService, ConfigurationService], (window, config) => {
        const logger = new LoggingService(config, window);
        logger.init();

        return logger;
    });

    await DiContainer.init(di);

    // set debug state out-of-band so logging service can be consumed synchronously
    di.getAsync(FeatureFlagService)
        .then(ffService =>
            Promise.all([di.getAsync(LoggingService), ffService.isEnabled(Feature.DebugMode)])
        )
        .then(([logger, isDebugEnabled]) => {
            logger.isDebugEnabled = isDebugEnabled;
        });

    // TODO: consolidate auth service usage
    const channelAuthService = di.get(ChannelAuthenticationService);
    const authService = di.get(AuthenticationService);
    const http = di.get(TmHttpClient);
    const logoutManager = di.get(LogoutManager);
    const accountService = di.get(AccountService);
    const navService = di.get(NavigationService);
    const loggingService = di.get(LoggingService);

    channelAuthService.authEvent$.subscribe(event => {
        switch (event) {
            case AuthFlowEvent.TermsAndConditionsAccept:
                navService.navigate('terms-and-conditions-accept');
                break;
            case AuthFlowEvent.Done:
                navService.navigate('dashboard');
                break;
            default:
                break;
        }
    });

    navService.routeChange$.subscribe(({ route }) => {
        // always fetch service fresh in case the FF has changed the instance
        di.getAsync(AnalyticsService).then(analyticsService => {
            analyticsService.trackPageView(route);
        });
    });

    authService.authenticated$.subscribe(() => {
        const config = di.get(ConfigurationService);
        const coBrowserService = di.get(CoBrowseService);
        onAuthenticated(logoutManager, accountService, loggingService, config, coBrowserService);
    });
    http.sessionExpired$.subscribe(() => onSessionEnd(di));
    http.error$.subscribe(err => {
        loggingService.logError('Fetch error', err);
    });
    logoutManager.logout$.subscribe(() => onSessionEnd(di));
}

async function onAuthenticated(
    logoutManager: LogoutManager,
    accountService: AccountService,
    loggingService: LoggingService,
    config: ConfigurationService,
    coBrowseService: CoBrowseService
) {
    // certain endpoint calls must be deferred until after login
    logoutManager.startLogoutTimers();

    const user = await accountService.getCurrentUser();
    loggingService.setUserSessionContext(user);

    const di = await DiContainer.getInstance();
    di.getAsync(AnalyticsService).then(analytics => {
        analytics.track(AnalyticsEvent.LogIn);
    });

    const ffService = await di.getAsync(FeatureFlagService);
    initFfService(ffService, accountService, config.institutionId).then(async () => {
        const coBrowseEnabled = await ffService.isEnabled(Feature.CoBrowse);
        if (coBrowseEnabled) {
            coBrowseService.init();
        }
    });
    const entitlementsService = await di.getAsync(EntitlementsService);
    entitlementsService.init();
}

function onSessionEnd(di: DiContainer) {
    const deps = [
        di.get(OmegaDialogService),
        di.get(NavigationService),
        di.get(AnalyticsService),
        di.get(RealtimeService),
        di.get(SessionStorageService),
        di.get(TmHttpClient),
        di.get(CoBrowseService),
    ] as const;

    return endSession(...deps);
}

async function endSession(
    dialogService: OmegaDialogService,
    navService: NavigationService,
    analyticsService: AnalyticsService,
    realtimeService: RealtimeService,
    sessionStorageService: SessionStorageService,
    httpClient: TmHttpClient,
    coBrowseService: CoBrowseService
) {
    realtimeService.disconnect();
    analyticsService.track(AnalyticsEvent.LogOut);
    analyticsService.reset();
    dialogService.closeAll();
    sessionStorageService.clear();
    coBrowseService.stop();

    if (httpClient instanceof JhHttpClientCached) {
        httpClient.clearCache();
    }

    const di = await DiContainer.getInstance();
    const ffService = await di.getAsync(FeatureFlagService);
    ffService.reset();

    navService.navigate('login').then(() => {
        // TODO: remove this once the deep link login issue has been resolved;
        // this is a temporary work around to clear in-memory state from a prior user session
        window.location.reload();
    });
}

async function initFfService(
    ffService: FeatureFlagService,
    accountService: AccountService,
    institutionId: string
) {
    const { loginId, isAdmin, isSuperUser, companyId } = await accountService.getCurrentUser();

    await ffService.init(institutionId, {
        userId: loginId,
        companyId,
        isAdmin,
        isSuperUser,
    });
}

const openDialogForSideDrawer: SideDrawerPromptFn = async (prompt, heading) => {
    const di = await DiContainer.getInstance();
    const dialogService = await di.getAsync(OmegaDialogService);
    return dialogService.open(prompt, heading).disposed;
};
